import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Icon } from "@iconify/react";
import io from "socket.io-client";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import "./watchlist.css";
import LiveChartModal from "./LiveChartModal";
import SearchableSelect from "../Common/SearchableSelect";
import AdminTradePanel from "./AdminTradePanel";
import { INDIAN_HOLIDAYS } from "../../utils/constant";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const SOCKET_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";
console.log("test changes")

const getMarketStatus = (segmentStr) => {
  const now = new Date();
  const ist = new Date(
    now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
  );
  const dateKey = ist.toISOString().split("T")[0];
  const day = ist.getDay();
  const isWeekend = day === 0 || day === 6;
  const time = ist.getHours() + ist.getMinutes() / 60;

  const isMatchHoliday = INDIAN_HOLIDAYS.includes(dateKey);

  const segment = (segmentStr || "").toUpperCase();
  let isOpen = false;
  let isHolidayStatus = false;
  let hoursText = "Opens Mon–Fri, 09:15–15:30 IST";

  if (segment.includes("CRYPTO")) {
    isOpen = true;
    hoursText = "Opens 24/7";
  } else if (segment.includes("FOREX") || segment.includes("COMEX")) {
    isOpen = !isWeekend;
    hoursText = "Opens Mon–Fri, 24 Hrs";
  } else if (segment.includes("MCX")) {
    isOpen = !isWeekend && !isMatchHoliday && time >= 9 && time <= 23.9;
    isHolidayStatus = !isWeekend && isMatchHoliday;
    hoursText = "Opens Mon–Fri, 09:00–23:55 IST";
  } else if (
    segment.includes("US-EQUITY") ||
    segment.includes("NASDAQ") ||
    segment.includes("NYSE")
  ) {
    isOpen = !isWeekend && (time >= 19 || time <= 1.5);
    hoursText = "Opens Mon–Fri, 19:00–01:30 IST";
  } else {
    // NSE/BSE
    isOpen = !isWeekend && !isMatchHoliday && time >= 9.25 && time < 15.5;
    isHolidayStatus = !isWeekend && isMatchHoliday;
    hoursText = "Opens Mon–Fri, 09:15–15:30 IST";
  }

  return { isOpen, isHoliday: isHolidayStatus, hoursText };
};

const HEAD_COLS = [
  { key: "bid", label: "BID", center: true },
  { key: "ask", label: "ASK", center: true },
  { key: "ltp", label: "LTP", center: false },
  { key: "chgPct", label: "CHG%", center: false },
  { key: "netChg", label: "NET CHG", center: false },
  { key: "high", label: "HIGH", center: false },
  { key: "low", label: "LOW", center: false },
  { key: "open", label: "OPEN", center: false },
  { key: "close", label: "CLOSE", center: false },
  { key: "chart", label: "CHART", center: true },
  { key: "remove", label: "REMOVE", center: true },
];

const WatchlistLayer = () => {
  const { AsyncGetApiCall, AsyncPostApiCall, AsyncDeleteApiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [lastTickTime, setLastTickTime] = useState(null);
  const [marketHealth, setMarketHealth] = useState({});
  const [allMasterSymbols, setAllMasterSymbols] = useState([]);
  const [liveData, setLiveData] = useState({});
  const [watchlist, setWatchlist] = useState([]);

  const [selSegment, setSelSegment] = useState("");
  const [segments, setSegments] = useState([]);
  const [selScript, setSelScript] = useState("");
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [activeTradePanelRow, setActiveTradePanelRow] = useState(null);
  const [initialTradeSide, setInitialTradeSide] = useState("BUY");
  const [adminClients, setAdminClients] = useState([]);

  const [selExpiry, setSelExpiry] = useState("");
  const [activeExpiries, setActiveExpiries] = useState([]);
  const [selStrike, setSelStrike] = useState("");
  const [activeStrikes, setActiveStrikes] = useState([]);
  const [selOptionType, setSelOptionType] = useState("CE");
  const selRef = useRef({
    script: selScript,
    segment: selSegment,
    expiry: selExpiry,
  });

  // 1. Update the selection ref immediately so the socket listener always has current context
  useEffect(() => {
    selRef.current = {
      script: selScript,
      segment: selSegment,
      expiry: selExpiry,
    };
  }, [selScript, selSegment, selExpiry]);

  // 2. ONLY reset discovered lists when the actual SCRIPT or SEGMENT changes.
  // This prevents the bug where selecting an expiry cleared all other options.
  useEffect(() => {
    setActiveExpiries([]);
    setActiveStrikes([]);
    setSelExpiry("");
    setSelStrike("");
  }, [selScript, selSegment]);

  const fetchWatchlist = async () => {
    const result = await AsyncGetApiCall("/trading-settings/watchlist/list");
    if (Array.isArray(result)) setWatchlist(result);
    else if (result?.success) setWatchlist(result.data || []);
  };

  const fetchAdminClients = async () => {
    try {
      const res = await AsyncGetApiCall(
        "/users/dynamic-list?listType=user&limitVal=1000",
      );
      if (res?.data) {
        setAdminClients(res.data || []);
      } else if (Array.isArray(res)) {
        setAdminClients(res);
      }
    } catch (err) {
      console.error("Error fetching clients", err);
    }
  };

  useEffect(() => {
    fetchAdminClients();
    // eslint-disable-next-line
  }, []);

  const fetchSymbols = async () => {
    const result = await AsyncGetApiCall("/trading-settings/scripts", {
      limitVal: 1000,
    });
    const data = Array.isArray(result) ? result : result?.data || [];
    setAllMasterSymbols(data);
  };

  const fetchSegments = async () => {
    const result = await AsyncGetApiCall(
      "/trading-settings/segments/list?status=active&inSelectMode=true",
    );
    if (result?.success) {
      setSegments(result?.data || []);
    }
    if (result?.data?.length > 0 && !selSegment) {
      setSelSegment(result?.data[0]?.name);
    }
  };
  //

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchWatchlist(), fetchSymbols(), fetchSegments()]).finally(
      () => setLoading(false),
    );
  }, []);

  /**
   * Helper to determine if a market tick (JSON object) matches a watchlist string entry.
   */
  const isSymbolMatch = (wStr, tick) => {
    if (!wStr || !tick) return false;
    const parts = wStr.split(" ");
    if (parts.length < 1) return false;

    const wName = parts[0];
    const wSegment = parts[parts.length - 1];
    const wExpiry = parts.length >= 3 ? parts[1] : null;

    const tName = (tick.name || tick.s || "").toUpperCase();
    let tSeg = tick.exchange || "";
    if (tSeg === "NOPT") tSeg = "NFO-OPT";
    else if (tSeg === "NFUT") tSeg = "NFO-FUT";
    else if (tSeg === "MCX") tSeg = "MCX-FUT";

    // 1. Basic Name & Segment Check
    if (tName !== wName.toUpperCase()) return false;
    if (tSeg && tSeg.toUpperCase() !== wSegment.toUpperCase()) return false;

    // 2. Expiry Check
    if (wExpiry && wExpiry !== "NO-EXPIRY" && tick.expiry) {
      if (tick.expiry !== wExpiry) return false;
    }

    // 3. Option Specific Check (Strike/Type)
    if (wSegment.includes("OPT") && parts.length >= 5) {
      const wStrike = parseFloat(parts[2]);
      const wOptType = parts[3].toUpperCase();

      let tStrike = parseFloat(tick.strike || "");
      let tOptType = (tick.type || "").toUpperCase();

      // If tick missing fields, try to extract from symbol
      if ((isNaN(tStrike) || !tOptType) && tick.symbol) {
        const match = /(\d+)\s?(CE|PE|CA|PA)$/i.exec(tick.symbol);
        if (match) {
          if (isNaN(tStrike)) tStrike = parseFloat(match[1]);
          if (!tOptType) tOptType = match[2].toUpperCase();
        }
      }

      // Now strictly compare
      if (isNaN(tStrike) || tStrike !== wStrike) return false;
      if (!tOptType || tOptType !== wOptType) return false;
    }
    return true;
  };

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      reconnection: true,
      transports: ["websocket"],
    });
    socket.on("connect", () => setIsConnected(true));
    socket.on("disconnect", () => setIsConnected(false));

    /**
     * Primary Market Data Listener (JSON)
     */
    socket.on("marketDataJson", (obj) => {
      setLastTickTime(Date.now());
      try {
        const name = obj.name || obj.s || obj.symbol;
        const exchange = obj.exchange;
        if (!name || !exchange) return;

        let segment = exchange;
        if (exchange === "NOPT") segment = "NFO-OPT";
        else if (exchange === "NFUT") segment = "NFO-FUT";
        else if (exchange === "MCX") segment = "MCX-FUT";

        // Track active expiries from the live feed for the selected script (Discovery Logic)
        if (obj.expiry) {
          const {
            script: curScript,
            segment: curSeg,
            expiry: curExpiry,
          } = selRef.current;

          const isMatch = (tName, tSeg, selName, selSeg) => {
            if (!tName || !selName || !tSeg || !selSeg) return false;
            const tn = tName.toUpperCase();
            const sn = selName.toUpperCase();
            const ts = tSeg.toUpperCase();
            const ss = selSeg.toUpperCase();

            if (ts !== ss) return false;

            if (tn === sn) return true;
            if (sn === "NIFTY" && (tn === "NIFTY 50" || tn === "NIFTY50"))
              return true;
            if (
              sn === "BANKNIFTY" &&
              (tn === "NIFTY BANK" || tn === "BANK NIFTY")
            )
              return true;
            return false;
          };

          if (isMatch(name, segment, curScript, curSeg)) {
            setActiveExpiries((prev) => {
              if (!prev.includes(obj.expiry))
                return [...prev, obj.expiry].sort();
              return prev;
            });

            if (!curExpiry || obj.expiry === curExpiry) {
              let stKey = obj.strike;
              let optType = obj.type;

              // Fallback: extract from symbol if missing
              if (
                (stKey === undefined || stKey === null || !optType) &&
                obj.symbol
              ) {
                const match = /(\d+)(CE|PE|CA|PA)$/.exec(obj.symbol);
                if (match) {
                  if (stKey === undefined || stKey === null) stKey = match[1];
                  if (!optType) optType = match[2];
                }
              }

              if (stKey !== undefined && stKey !== null) {
                const num = parseFloat(stKey);
                if (!isNaN(num)) {
                  setActiveStrikes((prev) => {
                    if (!prev.includes(num))
                      return [...prev, num].sort((a, b) => a - b);
                    return prev;
                  });
                }
              }
            }
          }
        }

        // Check if this incoming tick matches anything in our watchlist
        const matched = watchlist.some((w) => isSymbolMatch(w, obj));
        if (matched) {
          setLiveData((prev) => {
            const symKey = obj.symbol || `${name} ${segment}`;
            const old = prev[symKey] || {};
            // Preserve previous prices for flickering effect
            return {
              ...prev,
              [symKey]: {
                ...old,
                ...obj,
                prevBid: old.bid,
                prevAsk: old.ask,
                lastUpdate: Date.now(),
              },
            };
          });
        }
      } catch (e) { }
    });

    socket.on("marketHealthStatus", (h) => {
      setMarketHealth(h);
    });

    return () => socket.disconnect();
  }, [watchlist]);

  const scriptOptions = useMemo(() => {
    if (!Array.isArray(allMasterSymbols)) return [];
    const scripts = allMasterSymbols
      .filter((m) => m.segment === selSegment)
      .map((m) => m.name);
    return Array.from(new Set(scripts)).sort();
  }, [allMasterSymbols, selSegment]);

  const needsExpiry = useMemo(() => {
    if (!selSegment) return false;
    const expirySegments = [
      "NFO-OPT",
      "NFO-FUT",
      "MCX-FUT",
      "NOPT",
      "NFUT",
      "CDS",
    ];
    return (
      expirySegments.includes(selSegment) ||
      allMasterSymbols.some((m) => m.segment === selSegment && m.expiry)
    );
  }, [allMasterSymbols, selSegment]);

  const todayStr = useMemo(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  // Only include today's date for segments that don't typically have market expiries
  const expiryOptions = useMemo(() => {
    const opts = new Set(activeExpiries);
    const nonExpirySegments = ["CRYPTO", "FOREX", "NSE", "US-EQUITY"];
    if (nonExpirySegments.includes(selSegment)) {
      opts.add(todayStr);
    }
    return Array.from(opts).sort();
  }, [activeExpiries, todayStr, selSegment]);

  // Set default expiry from discovered options if available, otherwise blank for market segments
  useEffect(() => {
    if (!selExpiry && needsExpiry) {
      if (expiryOptions.length > 0) {
        // Find the one closest to now or just take the first one
        setSelExpiry(expiryOptions[0]);
      }
    }
  }, [needsExpiry, selExpiry, expiryOptions]);

  const handleAdd = async () => {
    if (!selScript) return;

    // Validation for segments that require an expiry
    if (needsExpiry && !selExpiry) {
      return toast.error("Please select an Expiry Date for this instrument");
    }

    let combined = `${selScript} ${selExpiry || "NO-EXPIRY"}`;
    if (selSegment && selSegment.includes("OPT")) {
      if (!selStrike || !selOptionType)
        return toast.error("Please select a Strike Price and Option Type");
      combined += ` ${selStrike} ${selOptionType}`;
    }
    combined += ` ${selSegment}`;

    combined = combined.replace(/\s+/g, " ").trim();
    if (!watchlist.includes(combined)) {
      const res = await AsyncPostApiCall("/trading-settings/watchlist/add", {
        symbol: combined,
      });
      if (res && res.success !== false && !res.error) {
        toast.success("Added to watchlist");
        fetchWatchlist();
      } else {
        toast.error(res?.message || "Failed to add instrument to watchlist");
      }
    }
  };

  const handleRemove = async (sym) => {
    const res = await AsyncDeleteApiCall(
      `/trading-settings/watchlist/remove?symbol=${encodeURIComponent(sym)}`,
    );
    if (res && res.success !== false && !res.error) {
      toast.success("Removed");
      fetchWatchlist();
    } else {
      toast.error(res?.message || "Failed to remove instrument");
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Clear all symbols?")) return;
    const res = await AsyncDeleteApiCall("/trading-settings/watchlist/clear");
    if (res && res.success !== false && !res.error) {
      toast.success("Watchlist cleared");
      setWatchlist([]);
    } else {
      toast.error(res?.message || "Failed to clear watchlist");
    }
  };

  const handleTradeSubmit = async (payload) => {
    try {
      const res = await AsyncPostApiCall("/orders/trade", payload);
      console.log("Trade Response:", res);
      if (res?.success) {
        toast.success("Trade placed successfully!");
        setActiveTradePanelRow(null);
        fetchAdminClients(); // Refresh client list to update balances/stats
      } else {
        const errorMsg =
          (res?.errors && res.errors[0]) ||
          res?.message ||
          res?.error ||
          "Failed to place order.";
        toast.error(errorMsg);
      }
      return res;
    } catch (err) {
      toast.error(err?.message || "Error placing order");
    }
  };

  const groupedWatchlist = useMemo(() => {
    const groups = {};
    const q = search.trim().toLowerCase();

    watchlist.forEach((item) => {
      const isObj = typeof item === "object" && item !== null;
      const sym = isObj ? item.symbol : item;
      const parts = sym.split(" ");
      const wName = isObj ? item.script : parts[0];
      const wSegment = isObj ? item.segment : parts[parts.length - 1];
      const wExpiry = isObj ? item.expiry : parts.length >= 3 ? parts[1] : null;

      if (
        q &&
        !sym.toLowerCase().includes(q) &&
        !String(item.exactSymbol || "")
          .toLowerCase()
          .includes(q)
      )
        return;

      const liveEntry = Object.values(liveData).find((ld) =>
        isSymbolMatch(isObj ? item.exactSymbol || sym : sym, ld),
      );

      const segment = wSegment || "OTHER";
      if (!groups[segment]) groups[segment] = [];

      const combinedRow = {
        name: wName,
        segment: wSegment,
        expiry: wExpiry,
        strike: isObj ? item.strike : parts.length === 5 ? parts[2] : null,
        optionType: isObj
          ? item.optionType
          : parts.length === 5
            ? parts[3]
            : null,
        exactSymbol: isObj ? item.exactSymbol : null,
        ...(liveEntry || {}),
        displaySymbol: isObj ? item.exactSymbol || sym : sym,
        originalSymbol: sym,
      };

      if (liveEntry) {
        groups[segment].push(combinedRow);
      } else {
        groups[segment].push({ ...combinedRow, isLoading: true });
      }
    });
    return groups;
  }, [watchlist, liveData, search]);

  const fmt = (v, d = 2) =>
    v != null && !isNaN(v) ? Number(v).toFixed(v < 10 && v > 0 ? 4 : d) : "---";

  const getFlickerClass = (current, prev, type) => {
    const base =
      type === "bid"
        ? "watchlist-price-pill watchlist-price-pill--bid"
        : "watchlist-price-pill watchlist-price-pill--ask";
    if (!prev || current === prev) return base;
    return current > prev
      ? `${base} watchlist-price-pill--flash-up`
      : `${base} watchlist-price-pill--flash-down`;
  };

  return (
    <>
      {loading && <Loader />}
      <div className="d-flex align-items-center gap-2 mb-24 watchlist-page-bar">
        <Icon
          icon="tabler:layout-grid"
          className="icon text-xl watchlist-page-bar-icon"
        />
        <Icon
          icon="tabler:chevron-right"
          className="icon text-sm watchlist-page-bar-icon"
        />
        <span className="fw-semibold watchlist-page-heading">
          Managed Watchlist
        </span>
        <div className="ms-auto d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-2 border-end border-secondary border-opacity-20 pe-3">
            <span className="watchlist-meta-label text-uppercase fw-bold">
              Data Feed
            </span>
            <span
              className={`badge ${isConnected ? "bg-success" : "bg-danger"} rounded-pill px-10 py-4`}
              style={{ fontSize: "10px" }}
            >
              {isConnected ? "CONNECTED" : "OFFLINE"}
            </span>
          </div>
          {lastTickTime && (
            <div className="d-flex align-items-center gap-2">
              <span className="watchlist-meta-label text-uppercase fw-bold">
                Freshness
              </span>
              <span className="watchlist-freshness-time fw-bold">
                {new Date(lastTickTime).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="card border shadow-sm radius-16 mb-24">
        <div className="watchlist-toolbar">
          <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <SearchableSelect
                placeholder="Select Segment"
                className="w-200-px"
                value={selSegment}
                onChange={(e) => {
                  setSelSegment(e.target.value);
                  setSelScript("");
                }}
                options={segments.map((s) => ({
                  label: s.displayName,
                  value: s.name,
                }))}
              />
              <SearchableSelect
                placeholder="Select script"
                className="w-200-px"
                options={scriptOptions.map((o) => ({ label: o, value: o }))}
                value={selScript}
                onChange={(e) => {
                  setSelScript(e.target.value);
                  setSelExpiry("");
                }}
                disabled={!selSegment}
              />
              {needsExpiry && (
                <SearchableSelect
                  placeholder="Expiry"
                  className="w-150-px"
                  options={expiryOptions.map((o) => ({ label: o, value: o }))}
                  value={selExpiry}
                  onChange={(e) => setSelExpiry(e.target.value)}
                  disabled={!selScript}
                />
              )}
              {selSegment && selSegment.includes("OPT") && (
                <>
                  <SearchableSelect
                    placeholder="Strike"
                    className="w-120-px"
                    options={activeStrikes.map((s) => ({
                      label: s.toString(),
                      value: s.toString(),
                    }))}
                    value={selStrike}
                    onChange={(e) => setSelStrike(e.target.value)}
                    disabled={!selExpiry}
                  />
                  <SearchableSelect
                    placeholder="Option Type"
                    className="w-120-px"
                    value={selOptionType}
                    onChange={(e) => setSelOptionType(e.target.value)}
                    disabled={!selStrike}
                    options={[
                      { label: "CE", value: "CE" },
                      { label: "PE", value: "PE" },
                    ]}
                  />
                </>
              )}
              <button
                type="button"
                className="btn btn-primary btn-sm radius-8 px-20 py-8 fw-bold"
                onClick={handleAdd}
              >
                Add
              </button>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm radius-8 px-16 py-8"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            </div>
            <form
              className="navbar-search"
              style={{
                maxWidth: "220px",
                width: "100%",
                marginLeft: "auto"
              }}
            >
              <input
                type="text"
                className="bg-base h-40-px w-100"
                name="search"
                placeholder="Filter List"
                onChange={(e) => setSearch(e.target.value)}
                value={search}
              />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
          </div>
        </div>

        <div className="card-body p-20 bg-base managed-watchlist">
          <div className="d-flex flex-column ">
            {Object.entries(groupedWatchlist).map(([segment, scripts]) => (
              <div key={segment} className="watchlist-accordion mb-3">
                <div className="d-flex align-items-center justify-content-between p-3 border-bottom watchlist-section-head cursor-pointer"
                  onClick={() =>
                    setCollapsed((prev) => ({
                      ...prev,
                      [segment]: !prev[segment],
                    }))
                  }
                >
                  <div className="d-flex align-items-center gap-2">
                    <span className="watchlist-segment-title fw-bold">
                      {segment}
                    </span>
                    <span className="badge watchlist-segment-count rounded-pill bg-primary">
                      {scripts.length}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="watchlist-hide-btn d-flex align-items-center gap-2"
                  >
                    <span>
                      {collapsed[segment] ? "SHOW" : "HIDE"}
                    </span>
                    <Icon icon={collapsed[segment] ? "ion:chevron-down" : "ion:chevron-up"} />
                  </button>
                </div>

                {!collapsed[segment] && (
                  <div className="table-responsive">
                    {scripts.length === 0 ? (
                      <div className="text-secondary text-center py-4 small">
                        No active real-time data for this segment.
                      </div>
                    ) : (
                      <table className="table table-hover align-middle mb-0 watchlist-data-table">
                        <thead className="table-light">
                          <tr>
                            <th className="watchlist-col-label fs-xs">INSTRUMENT</th>
                            {HEAD_COLS.map((col) => (
                              <th
                                key={col.key}
                                className={`watchlist-col-label fs-xs ${col.center ? "text-center" : ""}`}
                              >
                                {col.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {scripts.map((row) => (
                            <tr
                              key={row.displaySymbol}
                            >
                              <td>
                                <div className="fw-bold d-flex flex-column gap-1">
                                  <div className="w-100 d-flex gap-2">
                                    <span className="fs-14">{row.name}</span>
                                    {row.exactSymbol && (
                                      <span className="text-secondary fs-10 fw-normal opacity-50">
                                        {row.exactSymbol}
                                      </span>
                                    )}
                                  </div>
                                  <div className="w-100 d-flex gap-2">
                                    <div className="">
                                      <span className="badge bg-secondary-subtle text-secondary fs-10 px-6 py-2 rounded-1">
                                        {row.segment}
                                      </span>
                                    </div>
                                    {row.expiry &&
                                      row.expiry !== "NO-EXPIRY" &&
                                      [
                                        "NFO-OPT",
                                        "NFO-FUT",
                                        "MCX-FUT",
                                        "MCX-OPT",
                                        "CDS",
                                        "NOPT",
                                        "NFUT",
                                      ].includes(row.segment) && (
                                        <span className="text-secondary fs-10 d-block mt-1 fw-medium">
                                          EXP: {row.expiry}
                                        </span>
                                      )}
                                    {row.segment.includes("OPT") &&
                                      (row.strike || row.optionType) && (
                                        <span className="text-primary-600 fs-10 fw-bold ms-1">
                                          {row.strike} {row.optionType}
                                        </span>
                                      )}
                                  </div>
                                </div>
                              </td>

                              {row.isLoading ? (
                                <td colSpan={HEAD_COLS.length}>
                                  {(() => {
                                    const { isOpen, isHoliday, hoursText } =
                                      getMarketStatus(segment);
                                    const health =
                                      marketHealth[segment] || "UNKNOWN";

                                    return (
                                      <div className="d-flex align-items-center justify-content-between w-100">
                                        <div className="d-flex align-items-center gap-2 ps-3">
                                          {isHoliday ? (
                                            <>
                                              <span className="badge bg-danger-subtle text-danger border border-danger rounded-pill px-10 py-4 fw-semibold fs-xs d-flex align-items-center gap-1">
                                                <Icon
                                                  icon="tabler:calendar-off"
                                                  style={{ fontSize: "12px" }}
                                                />
                                                Market Holiday
                                              </span>
                                              <span className="text-secondary small fst-italic">
                                                No trading session today
                                              </span>
                                            </>
                                          ) : health === "INACTIVE" ? (
                                            <>
                                              <span className="badge bg-secondary-subtle text-secondary border border-secondary rounded-pill px-10 py-4 fw-semibold fs-xs d-flex align-items-center gap-1">
                                                <Icon
                                                  icon="tabler:plug-off"
                                                  style={{ fontSize: "12px" }}
                                                />
                                                No Data Feed
                                              </span>
                                              <span className="text-secondary small fst-italic">
                                                Feed is currently inactive for this segment
                                              </span>
                                            </>
                                          ) : health === "IDLE" ? (
                                            <>
                                              <span className="badge bg-info-subtle text-info border border-info rounded-pill px-10 py-4 fw-semibold fs-xs d-flex align-items-center gap-1">
                                                <Icon
                                                  icon="tabler:zzz"
                                                  style={{ fontSize: "12px" }}
                                                />
                                                Market Idle
                                              </span>
                                              <span className="text-secondary small fst-italic">
                                                Waiting for next market movement...
                                              </span>
                                            </>
                                          ) : isOpen ? (
                                            <>
                                              <div
                                                className="spinner-border spinner-border-sm text-info opacity-75"
                                                role="status"
                                              />
                                              <span className="text-secondary fs-sm fst-italic">
                                                Waiting for live tick...
                                              </span>
                                            </>
                                          ) : (
                                            <>
                                              <span className="badge bg-warning-subtle text-warning-emphasis border border-warning rounded-pill px-10 py-4 fw-semibold fs-xs d-flex align-items-center gap-1">
                                                <Icon
                                                  icon="tabler:moon"
                                                  className="icon"
                                                  style={{ fontSize: "12px" }}
                                                />
                                                Market Closed
                                              </span>
                                              <span
                                                className="text-secondary"
                                                style={{ fontSize: "0.72rem" }}
                                              >
                                                {hoursText}
                                              </span>
                                            </>
                                          )}
                                        </div>
                                        <div className="d-flex align-items-center justify-content-center pe-3">
                                          <button
                                            type="button"
                                            className="btn btn-link link-danger p-0 border-0"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRemove(row.displaySymbol);
                                            }}
                                          >
                                            <Icon
                                              icon="tabler:trash"
                                              className="icon fs-5"
                                            />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </td>
                              ) : (
                                <>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className={`${getFlickerClass(row.bid, row.prevBid, "bid")} price-btn`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setInitialTradeSide("SELL");
                                        setActiveTradePanelRow(row.displaySymbol);
                                      }}
                                      title={fmt(row.bid)}
                                    >
                                      {fmt(row.bid) || "—"}
                                    </button>
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className={`${getFlickerClass(row.ask, row.prevAsk, "ask")} price-btn`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setInitialTradeSide("BUY");
                                        setActiveTradePanelRow(row.displaySymbol);
                                      }}
                                    >
                                      {fmt(row.ask)}
                                    </button>
                                  </td>
                                  <td className="fw-bold">
                                    {fmt(row.ltp)}
                                  </td>
                                  <td className={`fw-bold ${row.chp >= 0 ? "text-success" : "text-danger"}`}>
                                    {row.chp > 0 ? "+" : ""}
                                    {fmt(row.chp)}%
                                  </td>
                                  <td className={`${row.ch >= 0 ? "text-success" : "text-danger"}`}>
                                    {row.ch > 0 ? "+" : ""}
                                    {fmt(row.ch)}
                                  </td>
                                  <td className="watchlist-cell-muted">
                                    {fmt(row.high)}
                                  </td>
                                  <td className="watchlist-cell-muted">
                                    {fmt(row.low)}
                                  </td>
                                  <td className="watchlist-cell-muted">
                                    {fmt(row.open)}
                                  </td>
                                  <td className="watchlist-cell-muted">
                                    {fmt(row.close)}
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-info radius-4 p-4 d-inline-flex align-items-center"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSymbol(row.displaySymbol);
                                        setShowModal(true);
                                      }}
                                    >
                                      <Icon
                                        icon="tabler:chart-line"
                                        className="fs-5"
                                      />
                                    </button>
                                  </td>
                                  <td className="text-center pe-3">
                                    <button
                                      type="button"
                                      className="btn btn-link link-danger p-0 border-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemove(row.displaySymbol);
                                      }}
                                    >
                                      <Icon
                                        icon="tabler:trash"
                                        className="icon fs-5"
                                      />
                                    </button>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}

            {watchlist.length === 0 && (
              <div className="text-center py-5 text-secondary border border-dashed border-secondary border-opacity-25 radius-12">
                Your watchlist is empty. Select a script above and click "Add".
              </div>
            )}
            {watchlist.length > 0 &&
              Object.keys(groupedWatchlist).length === 0 && (
                <div className="text-center py-5 text-secondary border border-dashed border-secondary border-opacity-25 radius-12 italic">
                  Waiting for real-time market data... Keep the page open to see
                  live prices.
                </div>
              )}
          </div>
        </div>
      </div >
      {/* {console.log("liveData-=-=-==>>>", liveData)} */}
      {
        showModal && (
          <LiveChartModal
            show={showModal}
            onHide={() => setShowModal(false)}
            symbol={selectedSymbol}
            data={liveData[selectedSymbol]}
            fmtPrice={fmt}
          />
        )
      }

      {
        activeTradePanelRow && (
          <AdminTradePanel
            show={!!activeTradePanelRow}
            symbolData={Object.values(groupedWatchlist).flat().find(r => r.displaySymbol === activeTradePanelRow)}
            scriptInfo={{ lotSize: Object.values(groupedWatchlist).flat().find(r => r.displaySymbol === activeTradePanelRow)?.ls || 1 }}
            clients={adminClients}
            initialSide={initialTradeSide}
            onClose={() => setActiveTradePanelRow(null)}
            onSubmit={handleTradeSubmit}
          />
        )
      }
    </>
  );
};

export default WatchlistLayer;
